import {
  ClientOrganizationRoleEnum,
  ClientOrganizationTagsEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Divider } from 'react-native-paper';

import { useGetOrganizationById } from '@/features/api/hooks/grapevine-organization/useGetOrganizationById';
import type { PostFeedTypeFeature } from '@/features/feature-management/types/postTypeFeature';
import type { useMediaPlaceholders } from '@/features/media/hooks/useMediaPlaceholder';
import { PostAmtlicheMeldungSwitch } from '@/features/post-editor/components/editor/PostAmtlicheMeldungSwitch';
import { PostCategoryField } from '@/features/post-editor/components/editor/PostCategoryField';
import { PostCategoryModal } from '@/features/post-editor/components/editor/PostCategoryModal';
import { PostChannelSection } from '@/features/post-editor/components/editor/PostChannelSection';
import { PostChannelTypeGuard } from '@/features/post-editor/components/editor/PostChannelTypeGuard';
import { PostDateField } from '@/features/post-editor/components/editor/PostDateField';
import { PostDateModal } from '@/features/post-editor/components/editor/PostDateModal';
import { PostFormProvider } from '@/features/post-editor/components/editor/PostFormProvider';
import { PostGeoAreaVisibilityField } from '@/features/post-editor/components/editor/PostGeoAreaVisibilityField';
import { PostLocationField } from '@/features/post-editor/components/editor/PostLocationField';
import { PostLocationModal } from '@/features/post-editor/components/editor/PostLocationModal';
import { PostTextField } from '@/features/post-editor/components/editor/PostTextField';
import { PostTitleField } from '@/features/post-editor/components/editor/PostTitleField';
import { PostVisibilitySwitch } from '@/features/post-editor/components/editor/PostVisibilitySwitch';
import { PostForm as PostFormSection } from '@/features/post-editor/constants/form';
import type { useEditorModal } from '@/features/post-editor/hooks/useEditorModal';
import type { PostCreationChannelOption } from '@/features/post-editor/types/postCreationOptions';
import type { PostFormValues } from '@/features/post-editor/types/postForm';
import { PostFeedType } from '@/features/posts/types/postFeed';
import { SenderGuard } from '@/features/sender-selection/components/SenderGuard';
import type { Sender } from '@/features/sender-selection/types/sender';
import { SenderType } from '@/features/sender-selection/types/sender';
import { OrganizationAccessGuard } from '@/shared/components/organization/OrganizationAccessGuard';
import { isOrganizationType } from '@/shared/utils/organization';
import { isEmptyOrNull } from '@/shared/utils/string';

const ORG_POSTER_ROLES = [
  ClientOrganizationRoleEnum.Admin,
  ClientOrganizationRoleEnum.Funker
];

type Props = {
  form: UseFormReturn<PostFormValues>;
  sender: Sender;
  media: ReturnType<typeof useMediaPlaceholders>;
  groupId?: string;
  modal: ReturnType<typeof useEditorModal>;
  channelReadonly?: boolean;
  geoAreaDepth?: number;
  senderChannels?: PostCreationChannelOption[];
  senderChannelOptions?: PostFeedTypeFeature[];
};

const OrganizationPostFields: FC<{
  organizationId: string;
  geoAreaDepth?: number;
}> = ({ organizationId, geoAreaDepth }) => {
  const { data: organization } = useGetOrganizationById(organizationId);

  const isMinistry = isOrganizationType(
    ClientOrganizationTagsEnum.Ministry,
    organization
  );
  const resolvedDepth = isMinistry ? geoAreaDepth : undefined;

  return (
    <>
      <PostChannelTypeGuard type={PostFeedType.NEWS_ITEM}>
        <OrganizationAccessGuard
          organization={organization}
          roles={ORG_POSTER_ROLES}
          requiredTags={[ClientOrganizationTagsEnum.TerritorialAuthority]}
        >
          <PostAmtlicheMeldungSwitch />
        </OrganizationAccessGuard>
      </PostChannelTypeGuard>
      <PostGeoAreaVisibilityField
        organizationId={organizationId}
        depth={resolvedDepth}
      />
    </>
  );
};

export const PostForm: FC<Props> = ({
  form,
  sender,
  media,
  groupId,
  modal,
  channelReadonly = false,
  geoAreaDepth,
  senderChannels,
  senderChannelOptions
}) => {
  const isOrganization = sender.type === SenderType.ORGANIZATION;
  return (
    <PostFormProvider form={form}>
      <PostChannelSection
        groupId={groupId}
        isOrganization={isOrganization}
        readonly={channelReadonly}
        senderChannels={senderChannels}
        senderChannelOptions={senderChannelOptions}
      />

      <PostTitleField />

      <Divider />

      <PostTextField
        images={media.mediaItemPlaceholders}
        isImageUploading={media.isImageUploadPending}
        onPickImage={media.onPickImage}
        onDeleteImage={media.onRemoveImage}
        onReorderImages={media.onReorderImages}
        documents={media.documentItemPlaceholders}
        maxDocuments={media.attachmentFeatureValues.maxNumberAttachmentsPerPost}
        isDocumentUploading={media.isDocumentUploadPending}
        onPickDocument={media.onPickDocument}
        onDeleteDocument={media.onRemoveDocument}
      />

      <Divider />

      <SenderGuard sender={sender} type={SenderType.ORGANIZATION}>
        {(orgSender) => (
          <OrganizationPostFields
            organizationId={orgSender.organizationId}
            geoAreaDepth={geoAreaDepth}
          />
        )}
      </SenderGuard>

      <PostChannelTypeGuard type={PostFeedType.HAPPENING}>
        <PostDateField onPress={() => modal.open(PostFormSection.DATE)} />
      </PostChannelTypeGuard>

      <PostChannelTypeGuard
        type={[PostFeedType.HAPPENING, PostFeedType.SUGGESTION]}
      >
        <PostLocationField
          onPress={() => modal.open(PostFormSection.LOCATION)}
        />
      </PostChannelTypeGuard>

      <PostChannelTypeGuard type={PostFeedType.SUGGESTION}>
        <PostCategoryField
          onPress={() => modal.open(PostFormSection.CATEGORY)}
        />
      </PostChannelTypeGuard>

      {isEmptyOrNull(groupId) && (
        <SenderGuard sender={sender} type={SenderType.USER}>
          {() => <PostVisibilitySwitch />}
        </SenderGuard>
      )}

      <PostDateModal
        isVisible={modal.active === PostFormSection.DATE}
        onClose={modal.close}
      />
      <PostLocationModal
        isVisible={modal.active === PostFormSection.LOCATION}
        onClose={modal.close}
      />
      <PostCategoryModal
        isVisible={modal.active === PostFormSection.CATEGORY}
        onClose={modal.close}
      />
    </PostFormProvider>
  );
};
